import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from "react"
import { EditorContent, useEditor, type Editor } from "@tiptap/react"
import type { JSONContent } from "@tiptap/core"
import Document from "@tiptap/extension-document"
import Paragraph from "@tiptap/extension-paragraph"
import Text from "@tiptap/extension-text"
import { Placeholder, UndoRedo } from "@tiptap/extensions"
import { Rows3 } from "lucide-react"
import type { CameraPickerPreset } from "@/components/cameras/camera-picker-dialog"
import { useTranslation } from "@/hooks/use-translation"
import { cn } from "@/lib/utils"
import { CameraPresetsProvider } from "./camera-presets-context"
import { insertDictatedText } from "./dictation-insert"
import { EmptyEditorCaret } from "./empty-editor-caret"
import { InlineMentionCompletion } from "./inline-mention-completion"
import { PromptMention } from "./mention-node"
import { PromptBlock } from "./prompt-block"
import { createMentionSuggestion, type MentionPlacement } from "./suggestion"
import { docToText, textToDoc } from "./serialize"
import { TokenPaste } from "./token-paste"
import type { MentionItem } from "./types"

function mentionItemKeyPart(it: MentionItem): string {
  return `${it.category}:${it.id}:${it.stateId ?? ""}:${it.attrKey ?? ""}:${it.label}:${it.imageUrl ?? ""}`
}

export type PromptEditorHandle = {
  focus: () => void
  insertReference: (label: string) => void
  getDoc: () => JSONContent | null
  getSnapshot: () => PromptEditorSnapshot | null
  /** Apply text from outside (e.g. agent) even when the editor is focused. */
  setExternalText: (text: string) => void
  /**
   * Insert plain text at the caret, spacing it off the preceding word. Used by voice
   * dictation: unlike setExternalText it keeps what is already typed, mentions included.
   */
  insertTextAtCursor: (text: string) => void
}

export type PromptEditorSnapshot = {
  text: string
  doc: JSONContent
}

type Props = {
  valueText: string
  /** Сохранённый Tiptap-документ (источник правды UI); при наличии используется для инициализации. */
  valueDoc?: JSONContent | null
  /** Меняется при загрузке другого шота/версии — повод пересобрать содержимое. */
  seedKey?: string | number
  mentionItems: MentionItem[]
  onChange: (text: string, doc: JSONContent) => void
  className?: string
  /** Доп. классы для области редактирования (.pe-prosemirror). */
  contentClassName?: string
  placeholder?: string
  ariaLabel?: string
  spellCheck?: boolean
  /** Доп. контролы в панели редактора (например, выбор камеры) рядом с «+сабшот». */
  toolbarExtra?: ReactNode
  /** Показывать кнопку «+сегмент». Холст её скрывает (свободная генерация без посекундной разметки). */
  showSegmentButton?: boolean
  mentionPlacement?: MentionPlacement
  showMentionHint?: boolean
  /** Переопределяет текст подсказки @ (по умолчанию — shot.promptEditor.mentionHint). */
  mentionHintText?: string
  onMentionInsert?: (item: MentionItem) => void
  /** Вызывается при фокусе в области редактирования. */
  onFocus?: () => void
  /** Профиль сущности: при наличии valueDoc не пересобирать из valueText (plain text). */
  docFirst?: boolean
  /** Пресеты камеры для пикера в заголовке сабшот-блока (@/components/prompt-editor/prompt-block). */
  cameraPresets?: CameraPickerPreset[]
  /** Проект для ленивой загрузки камер в сегментном пикере. */
  cameraProjectId?: number | null
  onCameraPresetsLoaded?: (presets: CameraPickerPreset[]) => void
  /** Drag the toolbar top edge to resize the editor (entity profile in the agent column). */
  onToolbarResizePointerDown?: (e: ReactPointerEvent<HTMLElement>) => void
  toolbarResizeAriaLabel?: string
  /** Pixel height for .pe-prosemirror when the editor is user-resized. */
  contentHeight?: number
  /** Stretch to fill the parent column. Wins over contentHeight. */
  fillAvailable?: boolean
  /** false — просмотр с чипами, без правки и тулбара. */
  editable?: boolean
}

function isNonEmptyDoc(doc: JSONContent | null | undefined): doc is JSONContent {
  return Boolean(doc && Array.isArray(doc.content) && doc.content.length > 0)
}

function resolveEditorContent(
  valueDoc: JSONContent | null | undefined,
  valueText: string,
  items: MentionItem[],
  docFirst: boolean,
): JSONContent {
  if (docFirst && isNonEmptyDoc(valueDoc)) return valueDoc
  if (!docFirst && valueText.trim().length > 0) return textToDoc(valueText, items)
  if (isNonEmptyDoc(valueDoc)) return valueDoc
  return textToDoc(valueText, items)
}

function applyContentPreservingSelection(editor: Editor, next: JSONContent): void {
  const current = editor.getJSON()
  if (JSON.stringify(current) === JSON.stringify(next)) return
  const { from, to } = editor.state.selection
  const hadFocus = editor.isFocused
  editor.commands.setContent(next)
  if (hadFocus) {
    const max = editor.state.doc.content.size
    editor.commands.setTextSelection({ from: Math.min(from, max), to: Math.min(to, max) })
  }
}

export const PromptEditor = forwardRef<PromptEditorHandle, Props>(function PromptEditor(
  {
    valueText,
    valueDoc,
    seedKey,
    mentionItems,
    onChange,
    className,
    contentClassName,
    placeholder,
    ariaLabel,
    spellCheck = false,
    toolbarExtra,
    showSegmentButton = true,
    mentionPlacement = "below",
    showMentionHint = true,
    mentionHintText,
    onMentionInsert,
    onFocus,
    docFirst = false,
    cameraPresets = [],
    cameraProjectId = null,
    onCameraPresetsLoaded,
    onToolbarResizePointerDown,
    toolbarResizeAriaLabel,
    contentHeight,
    fillAvailable = false,
    editable = true,
  },
  ref,
) {
  const resolvedContentHeight = fillAvailable ? undefined : contentHeight
  const { t } = useTranslation("shot")
  const itemsRef = useRef<MentionItem[]>(mentionItems)
  itemsRef.current = mentionItems
  const placementRef = useRef<MentionPlacement>(mentionPlacement)
  placementRef.current = mentionPlacement
  const onMentionInsertRef = useRef(onMentionInsert)
  onMentionInsertRef.current = onMentionInsert
  const onFocusRef = useRef(onFocus)
  onFocusRef.current = onFocus
  const docRef = useRef<JSONContent | null | undefined>(valueDoc)
  docRef.current = valueDoc
  const docFirstRef = useRef(docFirst)
  docFirstRef.current = docFirst
  const externalDocSeed = docFirst ? valueDoc : null
  const docFirstTextSeed = docFirst ? valueText : null

  const lastEmitted = useRef<string>(valueText)

  const mentionItemsKey = useMemo(() => mentionItems.map(mentionItemKeyPart).join("|"), [mentionItems])

  // Strip @reference_* ids are positional — rebuild via textToDoc on strip edits remaps chip labels.
  // Catalog entities/cameras still rebuild when their source data arrives/changes.
  const catalogMentionItemsKey = useMemo(
    () =>
      mentionItems
        .filter((it) => it.category !== "reference")
        .map(mentionItemKeyPart)
        .join("|"),
    [mentionItems],
  )

  const extensions = useMemo(
    () => [
      Document,
      Paragraph,
      Text,
      UndoRedo,
      Placeholder.configure({ placeholder: placeholder ?? "" }),
      PromptBlock,
      PromptMention.configure({
        getMentionItems: () => itemsRef.current,
        getOnMentionInsert: () => onMentionInsertRef.current,
        suggestion: createMentionSuggestion(
          () => itemsRef.current,
          () => placementRef.current,
          () => onMentionInsertRef.current,
        ),
      }),
      EmptyEditorCaret,
      TokenPaste.configure({
        getItems: () => itemsRef.current,
      }),
      InlineMentionCompletion.configure({
        getItems: () => itemsRef.current,
        getOnMentionInsert: () => onMentionInsertRef.current,
      }),
    ],
    // Расширения создаём один раз; placeholder фиксируем на маунт.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )

  const editor = useEditor({
    extensions,
    // Как docToText: один \n между блоками, иначе copy даёт \n\n и TokenPaste плодит пустые paragraph.
    coreExtensionOptions: {
      clipboardTextSerializer: { blockSeparator: "\n" },
    },
    content: resolveEditorContent(valueDoc, valueText, mentionItems, docFirst),
    editable,
    editorProps: {
      attributes: {
        class: cn(
          "pe-prosemirror it2tv-scrollbar",
          fillAvailable && "!h-auto !min-h-0 !max-h-none flex-1 resize-none overflow-auto",
          contentClassName,
        ),
        spellcheck: String(spellCheck),
        "aria-label": ariaLabel ?? t("promptEditor.defaultAriaLabel"),
      },
      handleDOMEvents: {
        focus: () => {
          onFocusRef.current?.()
          return false
        },
      },
    },
    onUpdate: ({ editor }) => {
      if (!editor.isEditable) return
      const text = docToText(editor.getJSON())
      lastEmitted.current = text
      onChange(text, editor.getJSON())
    },
  })

  useEffect(() => {
    if (!editor) return
    editor.setEditable(editable)
  }, [editor, editable])

  useEffect(() => {
    if (!editor) return
    const next = resolveEditorContent(docRef.current, valueText, itemsRef.current, docFirstRef.current)
    const nextText = docToText(next)
    if (docFirstRef.current) {
      const currentText = docToText(editor.getJSON())
      if (currentText === nextText) {
        lastEmitted.current = nextText
        return
      }
      if (editor.isFocused && nextText === lastEmitted.current) return
    }
    lastEmitted.current = nextText
    applyContentPreservingSelection(editor, next)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seedKey, editor, externalDocSeed, docFirstTextSeed])

  useEffect(() => {
    if (!editor || docFirstRef.current) return
    if (valueText === lastEmitted.current) return
    // Tiptap is authoritative while focused. A React render can still carry the
    // previous controlled value right after an imperative insertion/Enter.
    if (editor.isFocused) return
    lastEmitted.current = valueText
    applyContentPreservingSelection(editor, textToDoc(valueText, itemsRef.current))
  }, [valueText, editor])

  const appliedCatalogMentionItemsKeyRef = useRef(catalogMentionItemsKey)
  useEffect(() => {
    if (!editor || docFirstRef.current) return
    if (appliedCatalogMentionItemsKeyRef.current === catalogMentionItemsKey) return
    if (valueText !== lastEmitted.current) return
    appliedCatalogMentionItemsKeyRef.current = catalogMentionItemsKey
    const next = textToDoc(valueText, itemsRef.current)
    lastEmitted.current = docToText(next)
    applyContentPreservingSelection(editor, next)
  }, [catalogMentionItemsKey, editor, valueText])

  useEffect(() => {
    if (!editor) return
    // Force mention node views to re-read thumbnails from mentionItems.
    editor.view.dispatch(editor.state.tr.setMeta("mentionItems", mentionItemsKey))
  }, [editor, mentionItemsKey])

  useImperativeHandle(
    ref,
    () => ({
      focus: () => editor?.commands.focus(),
      getDoc: () => editor?.getJSON() ?? null,
      getSnapshot: () => {
        if (!editor) return null
        const doc = editor.getJSON()
        return { text: docToText(doc), doc }
      },
      insertReference: (label) => {
        if (!editor) return
        const item = itemsRef.current.find((it) => it.label === label && it.category === "reference")
        if (item) {
          editor
            .chain()
            .focus()
            .insertContent([
              { type: "mention", attrs: { category: "reference", id: item.id, label: item.label } },
              { type: "text", text: " " },
            ])
            .run()
        } else {
          editor.chain().focus().insertContent(`${label} `).run()
        }
      },
      setExternalText: (text) => {
        if (!editor) return
        const next = textToDoc(text, itemsRef.current)
        const nextText = docToText(next)
        lastEmitted.current = nextText
        applyContentPreservingSelection(editor, next)
        onChange(nextText, editor.getJSON())
      },
      insertTextAtCursor: (text) => {
        if (!editor) return
        // insertContent, а не setContent: чипы меншенов переживают вставку, а onUpdate
        // сам синхронизирует стейт наверху.
        insertDictatedText(editor, text)
      },
    }),
    [editor, onChange],
  )

  const showToolbar = editable && (showSegmentButton || showMentionHint || Boolean(toolbarExtra))

  return (
    <CameraPresetsProvider projectId={cameraProjectId} presets={cameraPresets} onPresetsLoaded={onCameraPresetsLoaded}>
      <div
        className={cn("pe-editor relative", fillAvailable && "flex h-full min-h-0 flex-col overflow-hidden", className)}
        style={
          resolvedContentHeight != null
            ? { ["--pe-content-height" as string]: `${resolvedContentHeight}px` }
            : undefined
        }
      >
        {onToolbarResizePointerDown ? (
          <div
            role="separator"
            aria-orientation="horizontal"
            aria-label={toolbarResizeAriaLabel}
            className="absolute inset-x-0 top-0 z-10 h-1 cursor-ns-resize"
            onPointerDown={onToolbarResizePointerDown}
          />
        ) : null}
        {showToolbar ? (
          <div className={cn("pe-toolbar", fillAvailable && "shrink-0")} contentEditable={false}>
            {showSegmentButton ? (
              <button
                type="button"
                className="pe-toolbar__btn"
                title={t("promptEditor.wrapSegmentTitle")}
                onClick={() => editor?.chain().focus().wrapInPromptBlock().run()}
              >
                <Rows3 className="h-3.5 w-3.5" />
                {t("promptEditor.segment")}
              </button>
            ) : null}
            {showMentionHint ? (
              <span className="pe-toolbar__hint">{mentionHintText ?? t("promptEditor.mentionHint")}</span>
            ) : null}
            {toolbarExtra}
          </div>
        ) : null}
        <EditorContent
          editor={editor}
          className={
            fillAvailable
              ? "flex min-h-0 flex-1 flex-col overflow-hidden [&_.tiptap]:min-h-0 [&_.tiptap]:flex-1 [&_.tiptap]:overflow-auto"
              : undefined
          }
        />
      </div>
    </CameraPresetsProvider>
  )
})
